/**
 * Browser live layer — a single WebSocket to the relay at `/ws`, shared across
 * the whole SPA.
 *
 *  - One socket, reconnecting with capped exponential backoff.
 *  - Local ref-counting: many components can `subscribe` the same (channel,
 *    contest); we only send one `{action:"subscribe"}` upstream on the 0→1
 *    transition and one `{action:"unsubscribe"}` on 1→0. On reconnect we re-send
 *    the full current subscription set.
 *  - A React store (`useRelayStatus`) exposes socket + upstream status for the
 *    footer WS dot, and a freshness store (`useLiquidityAge`) tracks when each
 *    contest's liquidity last changed for the staleness badge.
 *
 * The react-query bridge (`useLiveQueryBridge`) merges `liquidity.update` deltas
 * and `contest_state` pushes into the query cache; mount it once at the app root.
 */
import { useEffect, useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Arbitrage, LiquidityPosition, RestingOrder, RestingOrders, Signal } from "@greenbook/client";
import { authIdentity, hasScopeOrManual, isAuthed, resolveApiKey, SCOPES, subscribeAuth } from "./omauth.js";
import { STREAM_URL } from "./client.js";
import { forwardStreamFrame } from "./orderClient.js";

export type Channel = "liquidity" | "contest_state";
export type UpstreamStatus = "connected" | "connecting" | "down";
export type SocketState = "connecting" | "open" | "closed";
/** What the footer dot renders. */
export type RelayDisplay = "connected" | "reconnecting" | "down";

export interface RelayStatus {
  socket: SocketState;
  upstream: UpstreamStatus;
  display: RelayDisplay;
}

interface RelayMessage {
  type?: string;
  [k: string]: unknown;
}

interface LiquidityChange {
  liquidity_hash?: string;
  partner_id?: string;
  price?: number | null;
  available?: number | null;
}

/* -------------------------------------------------------------- status store */

let status: RelayStatus = { socket: "closed", upstream: "down", display: "down" };
const statusListeners = new Set<() => void>();

function computeDisplay(socket: SocketState, upstream: UpstreamStatus): RelayDisplay {
  if (socket === "open" && upstream === "connected") return "connected";
  if (socket === "closed" && upstream === "down") return "down";
  return "reconnecting"; // socket connecting, or upstream still spinning up
}

function setStatus(patch: Partial<Pick<RelayStatus, "socket" | "upstream">>): void {
  const socket = patch.socket ?? status.socket;
  const upstream = patch.upstream ?? status.upstream;
  const display = computeDisplay(socket, upstream);
  if (socket === status.socket && upstream === status.upstream && display === status.display) return;
  status = { socket, upstream, display };
  for (const l of statusListeners) l();
}

/* ----------------------------------------------------------- freshness store */
// Last time each contest's liquidity changed (push or successful refetch), so the
// UI can show a staleness badge when a feed goes quiet while the WS is down.

const freshness = new Map<string, number>();
const freshnessListeners = new Set<() => void>();

export function markLiquidityFresh(contestId: string): void {
  freshness.set(contestId, Date.now());
  for (const l of freshnessListeners) l();
}

/* -------------------------------------------------------- order pulse store */
// Router_order_ids that just changed via an order.update/settled push, so the resting
// monitor can flash the row for ~1.5s. Version-counter drives useSyncExternalStore.

const recentOrderIds = new Set<string>();
let orderPulseVersion = 0;
const orderPulseListeners = new Set<() => void>();

function bumpOrderPulse(): void {
  orderPulseVersion++;
  for (const l of orderPulseListeners) l();
}

function markOrderFresh(id: string): void {
  if (!id) return;
  recentOrderIds.add(id);
  bumpOrderPulse();
  setTimeout(() => {
    recentOrderIds.delete(id);
    bumpOrderPulse();
  }, 1500);
}

/** Set of router_order_ids that pulsed in the last ~1.5s (for flash-on-update). */
export function useOrderPulse(): Set<string> {
  useSyncExternalStore(
    (cb) => {
      orderPulseListeners.add(cb);
      return () => orderPulseListeners.delete(cb);
    },
    () => orderPulseVersion,
    () => orderPulseVersion,
  );
  return recentOrderIds;
}

/* --------------------------------------------------------------- relay client */

class RelayClient {
  private ws: WebSocket | null = null;
  private wantOpen = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** channel -> contestId -> local subscriber count. */
  private readonly counts = new Map<Channel, Map<string, number>>();
  /** Local subscriber count for the account-scoped `orders` channel (no contest ids). */
  private ordersCount = 0;
  /** Local subscriber count for the `arbitrage` channel. */
  private arbitrageCount = 0;
  /** Local subscriber count for the account-scoped `balance` channel (live balances). */
  private balanceCount = 0;
  /** Local subscriber count for the broad `signal` channel (live signals feed). */
  private signalCount = 0;
  private readonly handlers = new Set<(m: RelayMessage) => void>();
  /** Credential identity the current socket was opened with — to detect sign-in/out/swap. */
  private socketIdentity: string | null = null;
  /** Guards the async gap in ensureOpen so we never open two sockets. */
  private opening = false;

  constructor() {
    // React to auth changes (SDK session OR manual key): open when a credential connects, tear
    // down / reopen when the account changes or clears (never stream another user's data). A
    // silent key REFRESH keeps the same identity, so it does not churn the socket.
    subscribeAuth(() => this.onAuthChange());
  }

  private onAuthChange(): void {
    const next = authIdentity();
    if (next === this.socketIdentity) return;
    // Drop the old socket; reopen against the new credential if anything still wants data.
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
    this.socketIdentity = null;
    if (this.wantOpen && next) void this.ensureOpen();
    else setStatus({ socket: "closed", upstream: "down" });
  }

  private async ensureOpen(): Promise<void> {
    this.wantOpen = true;
    if (this.ws || this.opening) return;
    // No credential yet → don't open (the stream would 401). We reopen when auth connects.
    if (!isAuthed()) {
      setStatus({ socket: "closed", upstream: "down" });
      return;
    }
    // The plan may not grant real-time streaming. Opening anyway would just 401 and reconnect-
    // loop; instead stay "down" and let REST polling cover it (useLiveEnabled reads this).
    if (!hasScopeOrManual(SCOPES.stream)) {
      setStatus({ socket: "closed", upstream: "down" });
      return;
    }
    this.opening = true;
    setStatus({ socket: "connecting" });
    // Resolve a FRESH key (SDK getApiKey auto-renews); the WS auth rides in the query string.
    let key: string | null = null;
    try {
      key = await resolveApiKey();
    } catch {
      key = null;
    }
    this.opening = false;
    if (this.ws) return; // opened by a racing call
    if (!key) {
      setStatus({ socket: "closed", upstream: "down" });
      if (this.wantOpen) this.scheduleReconnect();
      return;
    }
    setStatus({ socket: "connecting" });
    this.socketIdentity = authIdentity();
    const ws = new WebSocket(`${STREAM_URL}?api_key=${encodeURIComponent(key)}`);
    this.ws = ws;
    ws.onopen = () => {
      this.reconnectAttempt = 0;
      // Socket is up; the server `connected` greeting (handled in onMessage) flips upstream
      // to "connected". Until then we're mid-handshake, not fully live.
      setStatus({ socket: "open", upstream: "connecting" });
      this.resubscribeAll();
    };
    ws.onmessage = (ev) => this.onMessage(ev.data);
    ws.onclose = () => {
      this.ws = null;
      setStatus({ socket: "closed", upstream: "down" });
      if (this.wantOpen) this.scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose will follow and drive reconnect.
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const attempt = this.reconnectAttempt++;
    const cap = Math.min(30_000, 1_000 * 2 ** attempt);
    const delay = Math.floor(cap / 2 + Math.random() * (cap / 2));
    setStatus({ socket: "connecting" });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wantOpen) this.ensureOpen();
    }, delay);
  }

  private onMessage(raw: unknown): void {
    let msg: RelayMessage;
    try {
      msg = JSON.parse(String(raw)) as RelayMessage;
    } catch {
      return;
    }
    // Direct-to-API status mapping (there's no relay control frame anymore): the server's
    // `connected` greeting means the upstream is fully live; a protocol `error` frame
    // (e.g. missing entitlement) is surfaced but doesn't tear the socket down.
    if (msg.type === "connected") {
      setStatus({ upstream: "connected" });
    } else if (msg.type === "error") {
      console.warn("[stream]", (msg as { code?: string }).code, (msg as { message?: string }).message);
    }
    // Forward every parsed frame (connected/subscribed/liquidity.update/order.*/
    // arbitrage.update/contest_state) to the app handlers — the react-query bridge relies on
    // seeing `connected` to re-fetch resting orders, and the rest to apply live deltas.
    for (const h of this.handlers) {
      try {
        h(msg);
      } catch {
        /* a bad handler must not kill the socket */
      }
    }
  }

  private send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }

  private channelCounts(channel: Channel): Map<string, number> {
    let m = this.counts.get(channel);
    if (!m) {
      m = new Map();
      this.counts.set(channel, m);
    }
    return m;
  }

  subscribe(channel: Channel, ids: string[]): void {
    if (ids.length === 0) return;
    this.ensureOpen();
    const m = this.channelCounts(channel);
    let changed = false;
    for (const id of ids) {
      const c = m.get(id) ?? 0;
      if (c === 0) changed = true;
      m.set(id, c + 1);
    }
    // The API treats a subscribe as a REPLACE of the channel's contest filter, so we must
    // send the FULL current union — not just the newly-added ids (unlike the old relay, which
    // maintained the union server-side).
    if (changed) this.send({ action: "subscribe", channel, contest_ids: [...m.keys()] });
  }

  unsubscribe(channel: Channel, ids: string[]): void {
    if (ids.length === 0) return;
    const m = this.counts.get(channel);
    if (!m) return;
    let changed = false;
    for (const id of ids) {
      const c = m.get(id) ?? 0;
      if (c <= 1) {
        m.delete(id);
        changed = true;
      } else {
        m.set(id, c - 1);
      }
    }
    if (!changed) return;
    // Re-send the reduced full union (REPLACE), or an explicit unsubscribe once empty.
    if (m.size > 0) this.send({ action: "subscribe", channel, contest_ids: [...m.keys()] });
    else this.send({ action: "unsubscribe", channel });
  }

  /** Subscribe the account-scoped orders channel (order.update / order.settled). */
  subscribeOrders(): void {
    this.ensureOpen();
    if (this.ordersCount === 0) this.send({ action: "subscribe", channel: "orders" });
    this.ordersCount++;
  }

  unsubscribeOrders(): void {
    if (this.ordersCount <= 0) return;
    this.ordersCount--;
    if (this.ordersCount === 0) this.send({ action: "unsubscribe", channel: "orders" });
  }

  /** Subscribe the arbitrage channel (broad; UI filters client-side). */
  subscribeArbitrage(): void {
    this.ensureOpen();
    if (this.arbitrageCount === 0) this.send({ action: "subscribe", channel: "arbitrage" });
    this.arbitrageCount++;
  }

  unsubscribeArbitrage(): void {
    if (this.arbitrageCount <= 0) return;
    this.arbitrageCount--;
    if (this.arbitrageCount === 0) this.send({ action: "unsubscribe", channel: "arbitrage" });
  }

  /**
   * Subscribe the account-scoped `balance` channel — live balance pushes so the displayed
   * `expected_balance` reflects orders created/cancelled/settled without waiting for the
   * ~60s balance re-read. Verified as a valid channel against the live stream (2026-08-24).
   */
  subscribeBalance(): void {
    this.ensureOpen();
    if (this.balanceCount === 0) this.send({ action: "subscribe", channel: "balance" });
    this.balanceCount++;
  }

  unsubscribeBalance(): void {
    if (this.balanceCount <= 0) return;
    this.balanceCount--;
    if (this.balanceCount === 0) this.send({ action: "unsubscribe", channel: "balance" });
  }

  /** Subscribe the broad `signal` channel — live `signal.update` pushes (per contest). */
  subscribeSignals(): void {
    this.ensureOpen();
    if (this.signalCount === 0) this.send({ action: "subscribe", channel: "signal" });
    this.signalCount++;
  }

  unsubscribeSignals(): void {
    if (this.signalCount <= 0) return;
    this.signalCount--;
    if (this.signalCount === 0) this.send({ action: "unsubscribe", channel: "signal" });
  }

  private resubscribeAll(): void {
    for (const [channel, m] of this.counts) {
      const ids = [...m.keys()];
      if (ids.length) this.send({ action: "subscribe", channel, contest_ids: ids });
    }
    if (this.ordersCount > 0) this.send({ action: "subscribe", channel: "orders" });
    if (this.arbitrageCount > 0) this.send({ action: "subscribe", channel: "arbitrage" });
    if (this.balanceCount > 0) this.send({ action: "subscribe", channel: "balance" });
    if (this.signalCount > 0) this.send({ action: "subscribe", channel: "signal" });
  }

  on(handler: (m: RelayMessage) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }
}

const relay = new RelayClient();

/* ----------------------------------------------------------------- react API */

export function useRelayStatus(): RelayStatus {
  return useSyncExternalStore(
    (cb) => {
      statusListeners.add(cb);
      return () => statusListeners.delete(cb);
    },
    () => status,
    () => status,
  );
}

/** Live subscription = relay socket open AND upstream connected. */
export function useLiveEnabled(): boolean {
  return useRelayStatus().display === "connected";
}

/** Subscribe to both channels for the given contest id(s) while mounted. */
export function useLiveContest(contestIds: string | string[] | undefined): void {
  const ids = normalizeIds(contestIds);
  const key = ids.join(",");
  useEffect(() => {
    if (ids.length === 0) return;
    relay.subscribe("liquidity", ids);
    relay.subscribe("contest_state", ids);
    return () => {
      relay.unsubscribe("liquidity", ids);
      relay.unsubscribe("contest_state", ids);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Subscribe only the contest_state channel (contest browser live chips). */
export function useLiveContestState(contestIds: string[]): void {
  const ids = normalizeIds(contestIds);
  const key = ids.join(",");
  useEffect(() => {
    if (ids.length === 0) return;
    relay.subscribe("contest_state", ids);
    return () => relay.unsubscribe("contest_state", ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Seconds since this contest's liquidity last changed (null if never seen). */
export function useLiquidityAge(contestId: string | undefined): number | null {
  const ts = useSyncExternalStore(
    (cb) => {
      freshnessListeners.add(cb);
      return () => freshnessListeners.delete(cb);
    },
    () => (contestId ? (freshness.get(contestId) ?? null) : null),
    () => null,
  );
  // Re-render on a coarse tick so the age advances even without new events.
  useEffect(() => {
    const t = setInterval(() => {
      for (const l of freshnessListeners) l();
    }, 5_000);
    return () => clearInterval(t);
  }, []);
  return ts === null ? null : Math.max(0, Math.round((Date.now() - ts) / 1000));
}

/**
 * Mount once at the app root: bridges relay pushes into the react-query cache.
 * liquidity.update → merge into ["liquidity", id]; refresh_required → invalidate.
 * contest_state → best-effort cache of ["state", id] (shape unobserved live — see
 * FRICTION.md — so if the push isn't state-like we invalidate to refetch typed REST).
 */
/**
 * Per-account_partner `balance_version` watermark. `balance.update` frames are monotonic per
 * `account_partner_id` but their delivery order is NOT guaranteed, so we drop any frame that
 * isn't strictly newer than the last we applied for that id (per /developers → Streaming) —
 * otherwise a late frame overwrites a newer balance. Cleared on (re)connect, where we refetch
 * fresh opening figures.
 */
const balanceVersions = new Map<string, number>();

/**
 * `balance.update` push (documented 2026-08-24). Practice (ATLAS) and real venues now emit the
 * SAME events. We drop stale/out-of-order frames by `balance_version`, then refetch the
 * authoritative account queries (which carry the corrected `expected_balance` + per-currency
 * totals). Refetch-per-event, not polling — events fire per order hold/release/settlement/
 * refresh (real) or wallet (practice), so it's cheap and stays consistent with no gap risk.
 */
function applyBalanceUpdate(qc: QueryClient, msg: RelayMessage): void {
  const apId = typeof msg.account_partner_id === "string" ? msg.account_partner_id : null;
  const ver = typeof msg.balance_version === "number" ? msg.balance_version : null;
  if (apId && ver != null) {
    const last = balanceVersions.get(apId);
    if (last != null && ver <= last) return; // out-of-order / duplicate → drop
    balanceVersions.set(apId, ver);
  }
  void qc.invalidateQueries({ queryKey: ["account"] });
}

export function useLiveQueryBridge(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const off = relay.on((msg) => {
      // The order SDK runs with its own stream OFF (one connection on the plan, and this
      // socket holds it) — hand it the order/balance frames it would have read itself.
      forwardStreamFrame(msg);
      if (msg.type === "liquidity.update") applyLiquidityUpdate(qc, msg);
      else if (typeof msg.type === "string" && msg.type.startsWith("contest_state"))
        applyContestState(qc, msg);
      else if (msg.type === "order.update") applyOrderUpdate(qc, msg);
      else if (msg.type === "order.settled") applyOrderSettled(qc, msg);
      else if (msg.type === "arbitrage.update") applyArbitrageUpdate(qc, msg);
      else if (msg.type === "signal.update") applySignalUpdate(qc, msg);
      else if (typeof msg.type === "string" && msg.type.startsWith("balance"))
        applyBalanceUpdate(qc, msg);
      else if (msg.type === "connected") {
        // Upstream (re)connected. These channels are "changes only" and don't replay state,
        // so pull fresh opening figures (per /developers → Streaming) and reset the balance
        // version watermarks so post-reconnect events apply cleanly.
        balanceVersions.clear();
        qc.invalidateQueries({ queryKey: ["orders", "resting"] });
        qc.invalidateQueries({ queryKey: ["arbitrages"] });
        qc.invalidateQueries({ queryKey: ["account"] });
      }
    });
    return off;
  }, [qc]);
}

/**
 * Subscribe the account-scoped orders channel while mounted — real-time order.update /
 * order.settled pushes (#667) that invalidate the order/account queries via the bridge.
 * Mount on pages that show order/resting/balance state (Orders, Desk).
 */
export function useLiveOrders(): void {
  useEffect(() => {
    relay.subscribeOrders();
    return () => relay.unsubscribeOrders();
  }, []);
}

/**
 * Subscribe the account-scoped `balance` channel while mounted — live balance pushes that
 * invalidate the account query so `expected_balance` updates in real time (no ~60s lag).
 * Mount once at the app root.
 */
export function useLiveBalances(): void {
  useEffect(() => {
    relay.subscribeBalance();
    return () => relay.unsubscribeBalance();
  }, []);
}

/**
 * `signal.update` push on the `signal` channel — per contest, carrying that contest's CURRENT
 * signals array (empty = cleared). We replace just that contest's signals in the ["signals"]
 * cache, so the live feed stays current without a full refetch. The REST poll (useSignals) is
 * the reconciling backstop and also drops time-expired signals.
 */
function applySignalUpdate(qc: QueryClient, msg: RelayMessage): void {
  const cid = typeof msg.contest_id === "string" ? msg.contest_id : null;
  if (!cid) return;
  const incoming = Array.isArray(msg.signals) ? (msg.signals as Signal[]) : [];
  qc.setQueryData<Signal[]>(["signals"], (old) => {
    const kept = (old ?? []).filter((s) => s.position?.router_contest_id !== cid);
    return [...kept, ...incoming];
  });
}

/**
 * Subscribe the broad `signal` channel while mounted — live signals feed. Mount once at the
 * app root so the ["signals"] cache stays current on the Signals page and inline on contests.
 */
export function useLiveSignals(): void {
  useEffect(() => {
    relay.subscribeSignals();
    return () => relay.unsubscribeSignals();
  }, []);
}

/**
 * Subscribe the arbitrage channel while mounted — `arbitrage.update` pushes merge into
 * the `["arbitrages"]` cache via the bridge. Mount on the Arbitrage page.
 */
export function useLiveArbitrage(): void {
  useEffect(() => {
    relay.subscribeArbitrage();
    return () => relay.unsubscribeArbitrage();
  }, []);
}

/* ------------------------------------------------------------- cache patchers */

function applyLiquidityUpdate(qc: QueryClient, msg: RelayMessage): void {
  const id = typeof msg.contest_id === "string" ? msg.contest_id : undefined;
  if (!id) return;
  const entries = Array.isArray(msg.changed_entries)
    ? (msg.changed_entries as LiquidityChange[])
    : [];

  if (entries.length) {
    const byHash = new Map<string, LiquidityChange>();
    for (const e of entries) if (e.liquidity_hash) byHash.set(e.liquidity_hash, e);

    // Patch every cache under ["liquidity", id]: the game page's full list AND the Markets
    // card's trimmed `{ positions }` (["liquidity", id, "card"]).
    const patch = (old: LiquidityPosition[]): LiquidityPosition[] => {
      let changed = false;
      const next = old.map((pos) => {
        let posChanged = false;
        const pls = (pos.partner_liquidities ?? []).map((pl) => {
          // Primary: match on liquidity_hash (carried on both sides). Fallback:
          // partner_id + the position_hash embedded as the hash suffix.
          const hit =
            (pl.liquidity_hash && byHash.get(pl.liquidity_hash)) ||
            entries.find(
              (e) =>
                e.partner_id === pl.partner_id &&
                typeof e.liquidity_hash === "string" &&
                e.liquidity_hash.endsWith(pos.position_hash),
            );
          if (hit && (hit.price !== pl.price || hit.available !== pl.available)) {
            posChanged = true;
            return { ...pl, price: hit.price ?? null, available: hit.available ?? null };
          }
          return pl;
        });
        if (posChanged) {
          changed = true;
          return { ...pos, partner_liquidities: pls };
        }
        return pos;
      });
      return changed ? next : old;
    };
    qc.setQueriesData<unknown>({ queryKey: ["liquidity", id] }, (old: unknown) => {
      if (Array.isArray(old)) return patch(old as LiquidityPosition[]);
      if (old && typeof old === "object" && Array.isArray((old as { positions?: unknown }).positions)) {
        const o = old as { positions: LiquidityPosition[] };
        const next = patch(o.positions);
        return next === o.positions ? old : { ...o, positions: next };
      }
      return old;
    });
    markLiquidityFresh(id);
  }

  // refresh_required is orthogonal to changed_entries (verified live) — invalidate
  // whether or not deltas were present.
  if (msg.refresh_required === true) {
    // Don't cancel-and-restart a download already in flight (apiGet can't abort, so a "cancelled"
    // ~3 MB fetch keeps downloading next to its replacement), and skip a refetch whose data is
    // only seconds old: subscribing sends refresh_required right as the first load is landing.
    const recent = qc
      .getQueryCache()
      .findAll({ queryKey: ["liquidity", id] })
      .some((q) => q.state.fetchStatus === "fetching" || Date.now() - q.state.dataUpdatedAt < REFRESH_SKIP_MS);
    if (!recent) void qc.invalidateQueries({ queryKey: ["liquidity", id] }, { cancelRefetch: false });
  }
}

/** A liquidity cache refreshed this recently already reflects a `refresh_required`. */
const REFRESH_SKIP_MS = 15_000;

function applyContestState(qc: QueryClient, msg: RelayMessage): void {
  const id = typeof msg.contest_id === "string" ? msg.contest_id : undefined;
  if (!id) return;
  // The push shape is unobserved live. If it carries a recognizable state body,
  // cache it directly; otherwise force a typed REST refetch we DO understand.
  if (msg.state !== undefined || "game_status" in msg) {
    qc.setQueryData(["state", id], msg);
  } else {
    void qc.invalidateQueries({ queryKey: ["state", id] });
  }
}

/**
 * order.update push (#667): `{router_order_id, status, fill_status, filled_amt,
 * unfilled_amt, avg_price}`. Patch the matching resting order in place for instant fill
 * progress; when it leaves the book (status no longer resting) drop it and refresh the
 * blotter + balances. An update for an order not in our snapshot → refetch resting.
 */
function applyOrderUpdate(qc: QueryClient, msg: RelayMessage): void {
  const id = typeof msg.router_order_id === "string" ? msg.router_order_id : undefined;
  if (!id) return;
  markOrderFresh(id);
  const status = String(msg.status ?? "").toLowerCase();
  const stillResting =
    status === "" || status === "resting" || status === "open" || status === "pending";

  let missing = false;
  qc.setQueryData<RestingOrders>(["orders", "resting"], (old) => {
    if (!old || !Array.isArray(old.orders)) return old;
    const has = old.orders.some((o) => o.router_order_id === id);
    if (!stillResting) return { ...old, orders: old.orders.filter((o) => o.router_order_id !== id) };
    if (!has) {
      missing = true; // a newly-resting order we didn't have → pull a fresh snapshot
      return old;
    }
    const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined);
    const orders = old.orders.map((o): RestingOrder =>
      o.router_order_id === id
        ? {
            ...o,
            fill_status: typeof msg.fill_status === "string" ? msg.fill_status : o.fill_status,
            filled_amt: num(msg.filled_amt) ?? o.filled_amt,
            unfilled_amt: num(msg.unfilled_amt) ?? o.unfilled_amt,
            price: num(msg.avg_price) ?? o.price,
          }
        : o,
    );
    return { ...old, orders };
  });

  if (missing || !stillResting) void qc.invalidateQueries({ queryKey: ["orders", "resting"] });
  // A fill moves money and adds to the blotter — but don't refetch resting (patched above).
  void qc.invalidateQueries({ queryKey: ["account"] });
  void qc.invalidateQueries({ queryKey: ["opportunities"] }); // opps are derived from orders
  void qc.invalidateQueries({
    queryKey: ["orders"],
    predicate: (query) => query.queryKey[1] !== "resting",
  });
}

/**
 * order.settled push (#667): the order left the book and settled. Drop it from resting
 * and refresh the blotter + balances so realized P&L/positions reflect the outcome.
 */
function applyOrderSettled(qc: QueryClient, msg: RelayMessage): void {
  const id = typeof msg.router_order_id === "string" ? msg.router_order_id : undefined;
  if (id) {
    markOrderFresh(id);
    qc.setQueryData<RestingOrders>(["orders", "resting"], (old) =>
      old && Array.isArray(old.orders)
        ? { ...old, orders: old.orders.filter((o) => o.router_order_id !== id) }
        : old,
    );
  }
  void qc.invalidateQueries({ queryKey: ["orders"], predicate: (query) => query.queryKey[1] !== "resting" });
  void qc.invalidateQueries({ queryKey: ["account"] });
  void qc.invalidateQueries({ queryKey: ["opportunities"] });
}

/**
 * arbitrage.update push: `{contest_id, arbitrages:[...]}` carries the FULL current arb
 * set for that contest (empty = none). Merge by contest id into the flat `["arbitrages"]`
 * list — drop that contest's existing arbs, add the incoming ones.
 */
function applyArbitrageUpdate(qc: QueryClient, msg: RelayMessage): void {
  const contestId = typeof msg.contest_id === "string" ? msg.contest_id : undefined;
  if (!contestId) return;
  const incoming = (Array.isArray(msg.arbitrages) ? msg.arbitrages : []) as Arbitrage[];
  qc.setQueryData<Arbitrage[]>(["arbitrages"], (old) => {
    const others = (Array.isArray(old) ? old : []).filter((a) => a?.contest?.id !== contestId);
    return [...others, ...incoming];
  });
}

/* --------------------------------------------------------------------- utils */

function normalizeIds(input: string | string[] | undefined): string[] {
  if (!input) return [];
  const arr = Array.isArray(input) ? input : [input];
  // Stable de-dup so the effect key is deterministic.
  return [...new Set(arr.filter(Boolean))].sort();
}
